import { MagnifyingGlass } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Input } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { localize } from 'common/utils';
import { FilterMenu } from 'modules/shared-menu/components/FilterMenu';
import { OFFER_FILTER_MENU_ITEMS } from 'modules/shared-menu/components/FilterMenu/constants';
import { SelectableOfferFilterId } from 'modules/shared-menu/components/FilterMenu/types';
import { SelectableAccordion, useSelectableAccordion } from 'modules/shared-menu/components/SelectableAccordion';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';

import { useFocusInputOnCtrlF } from '../../SharedMenuEditorPage/components/Collections/utils';

type Props = {
  searchFilter: string;
  setSearchFilter: (value: string) => void;
  setAppliedFilters: (value: SelectableOfferFilterId[]) => void;
  selectedCategoryId: string | undefined;
  setSelectedCategoryId: (value: string) => void;
};

const Root = styled.div`
  display: flex;
  padding: 24px;
  gap: 12px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledSelect = styled(Select)`
  flex-basis: 180px;
  flex-shrink: 0;
`;

const SearchInput = styled(Input)`
  background-color: ${color.bg};
`;

export const OffersFilters = ({
  searchFilter,
  setSearchFilter,
  setAppliedFilters,
  selectedCategoryId,
  setSelectedCategoryId,
}: Props) => {
  const intl = useIntl();
  const { categories } = useSelectionData();
  const { inputRef } = useFocusInputOnCtrlF();
  const {
    handleClearAll: clearAllFilters,
    handleGroupClick: toggleFilterGroup,
    handleItemClick: selectFilter,
    expandedGroups: expandedFilterGroups,
    expandAllGroups: expandAllFilterGroups,
    selectedIds: selectedFilterIds,
  } = useSelectableAccordion<SelectableOfferFilterId>(OFFER_FILTER_MENU_ITEMS, setAppliedFilters);

  const categoryOptions = [
    {
      value: '',
      label: intl.formatMessage({ id: 'shared-menu.editor.image-gallery.add-to-items.filters.all-categories' }),
    },
    ...categories.map(category => ({
      value: category.id,
      label: localize(category.name, 'en'),
    })),
  ];

  const categoryTitle = useMemo(() => {
    const selectedCategory = categories.find(category => category.id === selectedCategoryId);
    return selectedCategory
      ? localize(selectedCategory.name, intl.locale)
      : intl.formatMessage({ id: 'shared-menu.editor.image-gallery.add-to-items.filters.all-categories' });
  }, [categories, intl, selectedCategoryId]);

  return (
    <Root>
      <StyledSelect value={selectedCategoryId} onChange={setSelectedCategoryId} options={categoryOptions} />
      <SearchInput
        ref={inputRef}
        placeholder={intl.formatMessage({ id: 'menu.searchInCategory' }, { category: categoryTitle })}
        icon={<MagnifyingGlass />}
        value={searchFilter}
        onChange={event => setSearchFilter(event.currentTarget.value)}
      />
      <FilterMenu appliedFiltersCount={selectedFilterIds.length}>
        <SelectableAccordion
          items={OFFER_FILTER_MENU_ITEMS}
          expandedGroups={expandedFilterGroups}
          onItemClick={selectFilter}
          onGroupClick={toggleFilterGroup}
          onClearAllClick={clearAllFilters}
          expandAllGroups={expandAllFilterGroups}
          selectedIds={selectedFilterIds}
          expandedByDefault
        />
      </FilterMenu>
    </Root>
  );
};
